/*
 * Copyright (c) 2021 CalAmp Corp. All Rights Reserved
 */
import React, {useCallback, useState} from 'react';
import isNil from 'lodash.isnil';
import Chip from '@ion/dls/src/components/chip/Chip';
import ClickAwayListener from '@ion/dls/src/components/listeners/ClickAwayListener';
import Tooltip from '@ion/dls/src/components/tooltip/Tooltip';
import {MaterialExitCloseIcon} from '@ion/dls/src/icons/MaterialExitCloseIcon';
import {useGlobalProps} from '@ion/dls/src/props/useGlobalProps';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {IconSize} from '@ion/dls/src/types/types';
import {QueryChipData} from './types';
import {createLabelEllipsis} from './utils';

interface SimpleChipProps {
  query: QueryChipData;
  onRemove?: Function;
  disabled?: boolean;
  classes?: any;
  onDelete?: Function;
  radius?: string | number
  onClickChip?: (queryChipData: any) => void;
}

const chipHover = {
  maxHeight : '200px',
  overflow  : 'auto'
};

/**
 * Defines a reusable simple chip component for modular search component with tooltip.
 *
 * ```typescript
 * import React from 'react';
 * import {QueryChips} from '@ion/features/src/search/';
 *
 * <QueryChips queries={chips} onRemove={onQueryRemove} />
 * ```
 */

export const SimpleChip = ({
  query,
  onRemove,
  classes,
  onClickChip,
  radius = null,
  disabled = false
}: SimpleChipProps) => {
  const [openChips, setOpenChips] = useState(false);
  const hasTooltipStyle           = useGlobalProps('modularSearch.chip.hover.style') as boolean;

  const {
    value = {},
    label: queryLabel
  } = query;

  const label = value?.isTreeView ? `${value?.displayTitle}: ${value?.fullName}` : queryLabel;

  const {
    isEllipsis,
    label: shortLabel
  } = createLabelEllipsis(label);

  const onClick = useCallback(function() {
    onClickChip?.(query);
  }, [query]);

  const toggleTooltip = useCallback((open = false) => {
    return function(e) {
      e.preventDefault();
      if (!open) {
        setOpenChips(false);
      } else {
        setOpenChips(true);
      }
    };
  }, [openChips]);

  const chip = (
    <Chip isSelected={false}
      classes={classes}
      label={shortLabel}
      disable={disabled}
      onDelete={onRemove}
      isClickable={false}
      borderRadius={radius || 12}
      onClick={onClick}
      showCustomDeleteIcon={!isNil(onRemove)}
      customDeleteIcon={<MaterialExitCloseIcon size={IconSize.small}
        color={dlsColor('black')}
        stroke={dlsColor('black')} />
      } />
  );

  if (isEllipsis) {
    if (hasTooltipStyle) {
      return (
        <>
          <ClickAwayListener onClickAway={toggleTooltip()}
            mountListener>
            <Tooltip arrow
              open={openChips}
              onOpen={toggleTooltip(true)}
              onClose={toggleTooltip()}
              interactive={true}
              title={<div style={chipHover}>{label}</div>}>
              <div>{chip}</div>
            </Tooltip>
          </ClickAwayListener>
        </>
      );
    }
    return (
      <Tooltip arrow title={label} placement='top'>
        <div>
          {chip}
        </div>
      </Tooltip>
    );
  }

  return chip;
};
